import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import { api, formatEUR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const STATUS_LABEL = {
  pendiente: "Pendiente",
  preparando: "Preparando",
  enviado: "Enviado",
  entregado: "Entregado",
  cancelado: "Cancelado",
  // legacy fallback
  confirmed: "Pendiente",
  pending: "Pendiente",
  delivered: "Entregado",
  cancelled: "Cancelado",
};

const STATUS_CLASSES = {
  pendiente: "bg-neutral-200 text-neutral-700",
  preparando: "bg-amber-100 text-amber-800",
  enviado: "bg-blue-100 text-blue-800",
  entregado: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-red-100 text-red-800",
  confirmed: "bg-neutral-200 text-neutral-700",
  pending: "bg-neutral-200 text-neutral-700",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function MyOrdersPage() {
  const [orders, setOrders] = useState(null);

  const load = useCallback(() => {
    api
      .get("/orders")
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (orders === null) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-2xl bg-neutral-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div
      data-testid="my-orders-page"
      className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10"
    >
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-6">
        Mis pedidos
      </h1>

      {orders.length === 0 ? (
        <div
          data-testid="my-orders-empty"
          className="rounded-2xl border border-dashed border-neutral-300 p-12 text-center"
        >
          <div className="h-14 w-14 mx-auto rounded-full bg-neutral-100 grid place-items-center mb-3">
            <Package className="h-6 w-6 text-neutral-400" />
          </div>
          <p className="font-semibold text-lg">Aún no tienes pedidos</p>
          <p className="text-sm text-neutral-500 mt-1">
            Cuando hagas tu primera compra, aparecerá aquí.
          </p>
          <Button
            asChild
            className="mt-6 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            <Link to="/">Empezar a comprar</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const date = new Date(o.created_at);
            return (
              <Link
                key={o.id}
                to={
                  o.shop === "envio" || o.shop === "local"
                    ? `/${o.shop}/pedido/${o.id}`
                    : `/pedido/${o.id}`
                }
                data-testid={`order-row-${o.id}`}
                className="block rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#00953B] hover:shadow-sm transition-all"
              >
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div>
                    <div className="text-xs text-neutral-500">Pedido</div>
                    <div className="font-mono font-semibold">
                      #{o.id.slice(0, 8)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Fecha</div>
                    <div>{date.toLocaleDateString("es-ES")}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Campamento</div>
                    <div>{o.camp}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Estado</div>
                    <Badge
                      data-testid={`order-status-badge-${o.id}`}
                      className={STATUS_CLASSES[o.status] || "bg-neutral-200 text-neutral-700"}
                    >
                      {STATUS_LABEL[o.status] || o.status}
                    </Badge>
                  </div>
                  {/* Phase 32 — per-order storefront badge (Envío / Local). */}
                  {o.shop && (
                    <div>
                      <div className="text-xs text-neutral-500">Tienda</div>
                      <span
                        data-testid={`order-shop-badge-${o.id}`}
                        className={
                          o.shop === "local"
                            ? "inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold"
                            : "inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[11px] font-semibold"
                        }
                      >
                        {o.shop === "local" ? "🥫 Local" : "🇪🇸 Envío"}
                      </span>
                    </div>
                  )}
                  <div className="ml-auto text-right">
                    <div className="text-xs text-neutral-500">Total</div>
                    <div className="font-black text-lg">{formatEUR(o.total)}</div>
                  </div>
                </div>
                <div className="mt-2 text-[#00953B] text-sm font-semibold">
                  Ver detalle →
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
